import {
  Component,
  ContentChild,
  Inject,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { NtApiWorkOrder } from '../../../api/netsuite/types/work-order';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { AlertService } from '../../../core/services/alert.service';
import {
  BomDetailsExtraDataDirective,
  BomDetailsTitleDirective,
} from './bom-details.directive';
import { BomDataResponse, BomDetailsApiService } from './bom-details.service';
import { InspectionAssemblyItem } from '../../../api/elite/types/inspection-assembly-item';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { NetsuiteBom } from '../../../api/netsuite/models/bom';
import { BomDetailsTableComponent } from './table/table.component';
import { MatTabsModule } from '@angular/material/tabs';
import { BomAssemblyItem } from 'src/app/api/elite/types/bom-assembly';

export type BomDetailsDialogData = {
  searching: number | string;
  query?: string;
  title?: string;
  bomQuery?: string;
  includeInspectionSheet?: boolean;
  OnlyAssembly?: boolean;
};

@Component({
  selector: 'app-bom-details',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressBarModule,
    MatTableModule,
    MatIconModule,
    MatListModule,
    BomDetailsTableComponent,
    MatTabsModule,
  ],
  templateUrl: './bom-details.component.html',
  styleUrls: ['./bom-details.component.sass'],
})
export class BomDetailsComponent implements OnInit {
  @Input() displayedColumns: string[] = [
    'item',
    'description',
    'quantity',
    'onhand',
    'unitType',
    'verified',
  ];
  loading: boolean;
  measurementData: InspectionAssemblyItem = undefined;

  @Input()
  workOrderData: NtApiWorkOrder = undefined;

  @Input() batchQuantity = 0;

  bom: NetsuiteBom[] = [];
  lineBom: NetsuiteBom[] = [];

  showLineBom = true;

  @ContentChild(BomDetailsTitleDirective, { read: TemplateRef })
  templateTitle: TemplateRef<any> | undefined = undefined;

  @ContentChild(BomDetailsExtraDataDirective, { read: TemplateRef })
  templateExtraData: TemplateRef<any> | undefined = undefined;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: BomDetailsDialogData,
    private readonly apiRouter: BomDetailsApiService,
    private readonly alertService: AlertService,
    public dialogRef: MatDialogRef<any>
  ) {}

  ngOnInit(): void {
    this.setUpData();
  }

  private setUpData(): void {
    this.loading = true;
    if (!this.data.OnlyAssembly) {
      this.apiRouter.data(this.workOrderData, this.data).subscribe(
        (res) => {
          const { itemBom, linesBom, workOrderData } = res;
          this.workOrderData = workOrderData;
          console.log('batch', this.batchQuantity);
          this.bom = this.processBom(itemBom);
          this.lineBom = this.processBom(linesBom);

          this.showLineBom = this.hasBOMDifference();
          this.data.includeInspectionSheet
            ? this.setInspectionSheet()
            : (this.loading = false);
        },
        (err) => {
          console.error(err);
          this.dialogRef.close();
          this.alertService.error(
            'Error al cargar los datos, intentalo de nuevo'
          );
        }
      );
    } else {
      this.apiRouter
        .getAsemblyBomData(this.data.searching.toString())
        .subscribe({
          next: (data: BomAssemblyItem[]) => {
            const netsuiteBoms: NetsuiteBom[] = (data ?? []).map(
              this.toNetsuiteBom
            );
            console.log(netsuiteBoms);
            this.bom = netsuiteBoms;
            this.loading = false;
          },
          error: (err) => {
            console.error(err);
            this.loading = false;
          },
        });
    }
  }

  private processBom(itemBom: BomDataResponse): NetsuiteBom[] {
    let bom: NetsuiteBom[];
    if ('workOrderData' in itemBom) {
      bom = itemBom.bomData.bom;
    } else {
      bom = itemBom.bom;
    }
    if (!bom) {
      return [];
    }
    bom = bom.map((el) => {
      const bomLot = itemBom.bomLots.find(
        (lot) => lot.lotNumberOfBom === el.inventoryNumber
      );
      if (!bomLot) {
        return {
          ...el,
          quantityPerBatch: el.quantityPerAssembly * this.batchQuantity,
        };
      }
      return {
        ...el,
        verified: bomLot.validated,
        showValidation: true,
        quantityPerBatch: el.quantityPerAssembly * this.batchQuantity,
      };
    });
    return bom;
  }

  validateBomLot(bomLot: NetsuiteBom): void {
    this.apiRouter
      .validateBomLot(this.workOrderData.transactionId, bomLot.inventoryNumber)
      .subscribe(
        (res) => {
          this.alertService.smallSuccess('Bom Lot validado');
          this.bom = this.updateBomList(bomLot, this.bom);
          this.lineBom = this.updateBomList(bomLot, this.lineBom);
        },
        (err) => {
          console.error(err);
          this.alertService.smallError('Error al validar el bom lot');
        }
      );
  }

  private hasBOMDifference(): boolean {
    const itemBomMap = new Map(
      this.bom.map((item) => [item.itemId, item.quantityPerAssembly])
    );
    const lineBomMap = new Map(
      this.lineBom.map((item) => [item.itemId, item.quantityPerAssembly])
    );

    for (const [itemId, quantity] of itemBomMap) {
      const newQty = lineBomMap.get(itemId);

      if (newQty === undefined || newQty !== quantity) {
        return true;
      }
    }

    for (const itemId of lineBomMap.keys()) {
      if (!itemBomMap.has(itemId)) {
        return true;
      }
    }

    return false;
  }

  private setInspectionSheet(): void {
    this.loading = true;
    this.apiRouter.inspectionSheet(this.workOrderData.itemName).subscribe(
      (res) => {
        this.measurementData = res;
        this.loading = false;
      },
      (err) => {
        console.error(err);
        this.loading = false;
      }
    );
  }

  private updateBomList(
    bomLot: NetsuiteBom,
    bom: NetsuiteBom[]
  ): NetsuiteBom[] {
    return bom.map((el) => {
      if (el.itemId === bomLot.itemId) {
        return {
          ...el,
          verified: new Date(),
          showValidation: true,
        };
      }
      return el;
    });
  }
  private toNetsuiteBom(
    src: BomAssemblyItem
  ): NetsuiteBom & { quantityPerBatch: number } {
    // intenta extraer un nombre legible del material si viene como objeto
    const mat =
      typeof src.bomMaterial === 'string'
        ? src.bomMaterial
        : src?.bomMaterial?.name ?? src?.bomMaterial?.label ?? '';

    return {
      itemId: src.itemId,
      itemName: src.bomItemName,
      // si no trae bomItemDescription, usa itemDescription como respaldo
      itemDescription: src.bomItemDescription ?? src.itemDescription ?? '',
      quantityPerAssembly: src.bomQuantityNeededPerItem ?? 0,
      // No hay campos equivalentes claros en el origen: deja vacío o ajusta si tienes la unidad en otro campo
      unit: '',
      typeOfMaterial: mat,
      specialInstructions: '', // no existe en el origen
      inventoryNumber: '', // no existe en el origen
      quantityOnHand: src.bomQuantityOnHand ?? 0,
      upc: '', // no existe en el origen
      quantityPerBatch: src.bomQuantityNeededPerItem * this.batchQuantity,
      unitType: src.unitType,
    };
  }
}
